import { useApolloClient } from "@vue/apollo-composable";
import type { Ref } from "vue";
import { useRouter } from "vue-router";

import { useSwitchOrgMutation } from "../api/organization.api";

import { RouteNames } from "@/app/router/types/names";
import { tokenManager } from "@/shared/api/tokenManager";
import { useUserStore } from "@/shared/stores/useUserStore";

export interface UseSwitchOrganizationReturn {
  switchOrg: (orgId: string) => void
  switching: Ref<boolean>
}

/**
 * Switches the active organization (SuperAdmin flow): reissues the access token,
 * updates the user store, resets the Apollo cache and navigates to the org page.
 * No-op when targeting the already-active org or while a switch is in flight.
 */
export const useSwitchOrganization = (): UseSwitchOrganizationReturn => {
  const router = useRouter();
  const userStore = useUserStore();
  const { resolveClient } = useApolloClient();

  const { mutate, loading: switching, onDone } = useSwitchOrgMutation();

  onDone(async ({ data }) => {
    if (!data) return;
    tokenManager.setAccessToken(data.switchOrg.accessToken);
    userStore.setUser(data.switchOrg.user);
    await resolveClient().cache.reset();
    router.push({ name: RouteNames.ORGANIZATION });
  });

  const switchOrg = (orgId: string) => {
    if (orgId === userStore.orgId || switching.value) return;
    // Errors surface via the global Apollo error link; swallow the rejection so the
    // fire-and-forget call does not raise an unhandled promise rejection.
    void mutate({ orgId })?.catch(() => {});
  };

  return { switchOrg, switching };
};
